import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Warehouse } from '@models/warehouse.model';
import { useWarehouses } from '@hooks/api/warehouse';

interface WarehouseContextValue {
  warehouses: Warehouse[];
  selectedWarehouse: Warehouse | null;
  setSelectedWarehouse: (warehouse: Warehouse | null) => void;
  isLoading: boolean;
}

const WarehouseContext = createContext<WarehouseContextValue | undefined>(undefined);

export const useWarehouseContext = (): WarehouseContextValue => {
  const ctx = useContext(WarehouseContext);
  if (!ctx) {
    throw new Error('useWarehouseContext must be used within a WarehouseProvider');
  }
  return ctx;
};

const STORAGE_KEY = 'zh_selected_warehouse_id';

export const WarehouseProvider = ({ children }: { children: ReactNode }) => {
  const { data: warehouses = [], isLoading } = useWarehouses();
  const [selectedWarehouse, setSelectedWarehouseState] = useState<Warehouse | null>(null);

  // Wrap setter to persist selection in sessionStorage
  const setSelectedWarehouse = (warehouse: Warehouse | null) => {
    setSelectedWarehouseState(warehouse);
    if (warehouse) {
      sessionStorage.setItem(STORAGE_KEY, warehouse.warehouseId);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  };

  // Auto-select: restore from sessionStorage or fall back to default
  useEffect(() => {
    if (warehouses.length > 0 && !selectedWarehouse) {
      const savedId = sessionStorage.getItem(STORAGE_KEY);
      const savedWh = savedId ? warehouses.find((w) => w.warehouseId === savedId) : null;
      const fallback = warehouses.find((w) => w.isDefault) || warehouses[0];
      setSelectedWarehouse(savedWh || fallback);
    }
  }, [warehouses, selectedWarehouse]);

  // Keep selectedWarehouse in sync with warehouse list (e.g. after edit/delete)
  useEffect(() => {
    if (selectedWarehouse && warehouses.length > 0) {
      const updated = warehouses.find((w) => w.warehouseId === selectedWarehouse.warehouseId);
      if (updated) {
        // Refresh with latest data
        if (updated.name !== selectedWarehouse.name || updated.address !== selectedWarehouse.address || updated.isDefault !== selectedWarehouse.isDefault) {
          setSelectedWarehouseState(updated);
        }
      } else {
        // Warehouse was deleted, fallback to default
        const defaultWh = warehouses.find((w) => w.isDefault) || warehouses[0];
        setSelectedWarehouse(defaultWh);
      }
    }
  }, [warehouses, selectedWarehouse]);

  return (
    <WarehouseContext.Provider
      value={{
        warehouses,
        selectedWarehouse,
        setSelectedWarehouse,
        isLoading,
      }}
    >
      {children}
    </WarehouseContext.Provider>
  );
};
